import * as BABYLON from "@babylonjs/core/Legacy/legacy";

import { createPath, Path } from "./path.js";
import { Player } from "./player.js";
import { Asset } from "./asset";

import { Ground } from "./ground.js";

import { Scene2D } from "./gui/scene.js";
import { config } from "./config.js";

const log = console.log.bind(console);

export class GameScene {
    camera: BABYLON.ArcRotateCamera;
    path: Path;
    player: Player;
    ground: Ground;
    asset: Asset;

    createTime: number;

    isEnd: boolean = false;
    _loadTextureReady: boolean;
    isStart: boolean = false;
    bbScene: BABYLON.Scene;
    scene2d: Scene2D;
    speedMul: BABYLON.Vector3;

    avgDuration: number = 0;
    fps: number = 0;

    updateCallbacks: (() => void)[];

    constructor(engine: BABYLON.Engine, canvas: Element, asset?: Asset) {
        this.bbScene = new BABYLON.Scene(engine);
        this.createTime = new Date().getTime();

        let light = new BABYLON.HemisphericLight("light", new BABYLON.Vector3(0, 1, 0));
        let camera = new BABYLON.ArcRotateCamera("camera", -Math.PI / 2, Math.PI / 3.2, 3.2, new BABYLON.Vector3(0, 0, 0));

        if (process.env.NODE_ENV !== "production") {
            camera.attachControl(canvas, true);
        }

        camera.targetScreenOffset = new BABYLON.Vector2(0, -0.9);
        window.camera = camera;
        this.camera = camera;

        // 资源管理
        this.asset = new Asset(this);
        if (asset !== undefined) {
            this.asset.copyResoucesFrom(asset);
        }

        // 玩家
        this.player = new Player(this, this.camera);
        window.player = this.player;

        // 雾和环境
        this.setupEvironment();

        // 路径
        this.path = createPath(this, engine, this.camera, this.player);

        // 速度
        this.speedMul = BABYLON.Vector3.Zero();

        // update 函数
        this.updateCallbacks = [];

        // 显示调试面板
        if (process.env.NODE_ENV !== "production") {
            this.bbScene.debugLayer.show();
        }

        // 2d
        this.scene2d = new Scene2D(this, []);
        let scene = this.bbScene;

        // 水面
        this.ground = new Ground(this);

        let self = this;

        this.registerEngineUpdate();

        // 设置更新函数
        this.setupUpdate();

        // 事件
        this.bindSceneEvents();

        this._loadTextureReady = false;
    }

    start() {
        this.isStart = true;
    }

    public set loadTextureReady(b: boolean) {
        this._loadTextureReady = b;
    }

    public get loadTextureReady(): boolean {
        return this._loadTextureReady;
    }

    GetAsset(): Asset {
        return this.asset;
    }

    tryStart() {
        // texture 和 asset 都加载完成才能开始
        if (!this.isStart) {
            return;
        }
        if (!this.asset.isLoaded()) {
            return;
        }

        this.loadTextureReady = true;
        log("ready", this.loadTextureReady);
    }

    setupEvironment() {
        let color = new BABYLON.Color4(51 / 255, 82 / 255, 87 / 255, 1);
        this.bbScene.clearColor = color;

        let scene = this.bbScene;
        scene.fogColor = new BABYLON.Color3(51 / 255, 82 / 255, 87 / 255);
        // scene.fogColor = new BABYLON.Color3(1.0, 1.0, 1.0);

        // scene.fogMode = BABYLON.Scene.FOGMODE_EXP2;
        // scene.fogDensity = 0.22;

        scene.fogMode = BABYLON.Scene.FOGMODE_LINEAR;
        scene.fogDensity = 100;
        scene.fogStart = 4.0;
        scene.fogEnd = 9.0;
    }

    render() {
        this.bbScene.render();
    }

    private registerEngineUpdate() {
        let targetFps = 60;
        let frameTime = 1 / targetFps;
        let lastRender = +new Date();
        let updated = false;

        let self = this;
        // 注册更新函数
        this.bbScene.onBeforeRenderObservable.add(function () {
            self.updateCallbacks.forEach((f: () => void) => {
                f();
            });
        });
    }

    addUpdateCallback(f: () => void) {
        this.updateCallbacks.push(f);
    }

    update() {
        let self = this;
        // 2d scene
        self.scene2d.update();

        if (self.isEnd === true || !self.loadTextureReady) {
            return;
        }

        // 更新 path, 把新的路画上去，把旧的路回收
        self.path.update();
        // 更新水面的位置
        self.ground.update(self.player.pos2D());
        // 角色往前移动 speed
        let curSegment = self.path.getCurrentSegment();
        if (curSegment.contain(self.player.pos2D())) {
            self.player.update();
        }
    }

    setupUpdate() {
        // scene 的 update 函数
        // 每帧被调用
        let self = this;
        this.addUpdateCallback(function () {
            self.update();
        });

        // 碰撞检测应该放在这里
        this.bbScene.onAfterRenderObservable.add(function () {
            if (self.isEnd === true || !self.loadTextureReady) {
                return;
            }
            self.path.checkCollision();
        });
    }

    bindSceneEvents() {
        let self = this;
        // 给 scene 注册键盘事件, 目前还不能左右移动
        this.bbScene.onKeyboardObservable.add(function (event) {
            if (event.type === BABYLON.KeyboardEventTypes.KEYDOWN) {
                let curSegment = self.path.getCurrentSegment();

                // log('key', event.event.key)
                if (event.event.key === "a") {
                    curSegment.moveLeft(self.player);
                } else if (event.event.key === "d") {
                    curSegment.moveRight(self.player);
                } else if (event.event.key === "w") {
                    self.player.jump();
                } else if (event.event.key === "k") {
                    self.path.turn();
                } else if (event.event.key === "j") {
                    self.path.turn();
                } else if (event.event.key === " ") {
                    if (self.speedMul.x !== 0) {
                        self.speedMul = BABYLON.Vector3.Zero();
                    } else {
                        self.speedMul = new BABYLON.Vector3(1, 1, 1);
                    }
                }
            }
        });
    }
}
